import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Gavel, ArrowLeft, RefreshCw, Plus, Edit, Trash2, Eye, Play, Pause,
  DollarSign, Users, Clock, Trophy, CheckCircle, XCircle, Calendar, Radio, Tv, Sparkles, HardDrive
} from 'lucide-react';
import api, { getErrorMessage } from '../../utils/api';
import { toast } from 'sonner';
import { getImageUrl } from '../../utils/imageUrl';
import AIImportWidget from '../../components/AIImportWidget';
import GoogleDrivePicker from '../../components/GoogleDrivePicker';

const AuctionAdmin = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [auctions, setAuctions] = useState([]);
  const [stats, setStats] = useState({ total: 0, live: 0, upcoming: 0, completed: 0, total_bids: 0 });
  const [activeTab, setActiveTab] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingAuction, setEditingAuction] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    starting_bid: 100,
    bid_increment: 10,
    reserve_price: 0,
    buy_now_price: 0,
    start_time: '',
    end_time: '',
    images: [],
    image_url: '',
    category_id: 'electronics',
    entry_pass_tier: 'SILVER',
    allow_free_members: false  // NEW: Allow free members toggle
  });
  const [showAIImport, setShowAIImport] = useState(false);
  const [showDrivePicker, setShowDrivePicker] = useState(false);

  // Handle Google Drive selection
  const handleDriveSelect = (files) => {
    const fileUrls = files.map(f => f.url);
    setFormData(prev => ({
      ...prev,
      images: [...prev.images, ...fileUrls],
      image_url: fileUrls[0] || prev.image_url
    }));
    setShowDrivePicker(false);
    toast.success(`${fileUrls.length} image(s) added from Google Drive`);
  };

  // Handle AI Import data for auctions
  const handleAIImportData = (data) => {
    setFormData(prev => ({
      ...prev,
      title: data.name || prev.title,
      description: data.description || prev.description,
      starting_bid: data.price ? Math.floor(data.price * 0.5) : prev.starting_bid, // Start at 50% of price
      reserve_price: data.price || prev.reserve_price,
      buy_now_price: data.mrp || data.price * 1.2 || prev.buy_now_price,
      image_url: data.images && data.images[0] ? data.images[0] : prev.image_url,
      images: data.images || prev.images,
    }));
    setShowAIImport(false);
    toast.success('Auction form auto-filled with extracted data!');
  };

  useEffect(() => {
    fetchAuctions();
  }, [activeTab]);

  const fetchAuctions = async () => {
    setLoading(true);
    try {
      const status = activeTab !== 'all' ? `?status=${activeTab}` : '';
      const response = await api.get(`/admin/auctions${status}`);
      const data = response.data || [];
      setAuctions(Array.isArray(data) ? data : data.auctions || []);
      
      // Calculate stats
      const all = Array.isArray(data) ? data : data.auctions || [];
      setStats({
        total: all.length,
        live: all.filter(a => a.status === 'live').length,
        upcoming: all.filter(a => a.status === 'upcoming').length,
        completed: all.filter(a => a.status === 'completed').length,
        total_bids: all.reduce((acc, a) => acc + (a.bid_count || 0), 0)
      });
    } catch (error) {
      console.error('Failed to fetch auctions:', error);
      setAuctions([]);
    } finally {
      setLoading(false);
    }
  };

  const createAuction = async () => {
    try {
      await api.post('/admin/auctions', formData);
      toast.success('Auction created!');
      setShowCreateModal(false);
      resetForm();
      fetchAuctions();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create auction'));
    }
  };

  const updateAuction = async () => {
    try {
      await api.put(`/admin/auctions/${editingAuction.id}`, formData);
      toast.success('Auction updated!');
      setEditingAuction(null);
      resetForm();
      fetchAuctions();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update auction'));
    }
  };

  const deleteAuction = async (auctionId) => {
    if (!window.confirm('Are you sure you want to delete this auction?')) return;
    try {
      await api.delete(`/admin/auctions/${auctionId}`);
      toast.success('Auction deleted!');
      fetchAuctions();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete auction'));
    }
  };

  const toggleAuctionStatus = async (auctionId, currentStatus) => {
    const newStatus = currentStatus === 'live' ? 'paused' : 'live';
    try {
      await api.put(`/admin/auctions/${auctionId}`, { status: newStatus });
      toast.success(`Auction ${newStatus === 'live' ? 'started' : 'paused'}!`);
      fetchAuctions();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update status'));
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      starting_bid: 100,
      bid_increment: 10,
      reserve_price: 0,
      buy_now_price: 0,
      start_time: '',
      end_time: '',
      images: [],
      image_url: '',
      category_id: 'electronics',
      entry_pass_tier: 'SILVER',
      allow_free_members: false
    });
  };

  const openEditModal = (auction) => {
    setEditingAuction(auction);
    setFormData({
      title: auction.title || '',
      description: auction.description || '',
      starting_bid: auction.starting_bid || 100,
      bid_increment: auction.bid_increment || 10,
      reserve_price: auction.reserve_price || 0,
      buy_now_price: auction.buy_now_price || 0,
      start_time: auction.start_time || '',
      end_time: auction.end_time || '',
      images: auction.images || [],
      image_url: auction.images?.[0] || '',
      category_id: auction.category_id || 'electronics',
      entry_pass_tier: auction.entry_pass_tier || 'SILVER',
      allow_free_members: auction.allow_free_members || false
    });
  };

  const categories = ['electronics', 'fashion', 'home', 'sports', 'collectibles', 'vehicles', 'other'];

  return (
    <div className="min-h-screen pt-24 pb-16 px-6" data-testid="auction-admin">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <Link to="/admin" className="text-muted-foreground hover:text-white text-sm flex items-center gap-1 mb-2">
              <ArrowLeft className="w-4 h-4" /> Back to Admin
            </Link>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <Gavel className="w-8 h-8 text-orange-500" />
              Auction House Admin
            </h1>
          </div>
          <div className="flex gap-3">
            <Link 
              to="/display/auctions" 
              target="_blank"
              className="p-3 rounded-xl bg-purple-500/20 hover:bg-purple-500/30 text-purple-400"
              title="Open Display TV Mode"
            >
              <Tv className="w-5 h-5" />
            </Link>
            <button onClick={fetchAuctions} className="p-3 rounded-xl bg-white/10 hover:bg-white/20">
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button 
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-3 rounded-xl bg-orange-500 text-white font-semibold flex items-center gap-2 hover:bg-orange-600"
            >
              <Plus className="w-5 h-5" /> Create Auction
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <div className="glass-card p-5">
            <Gavel className="w-7 h-7 text-orange-500 mb-2" />
            <p className="text-2xl font-bold">{stats.total}</p>
            <p className="text-sm text-muted-foreground">Total Auctions</p>
          </div>
          <div className="glass-card p-5 border border-green-500/30">
            <Play className="w-7 h-7 text-green-500 mb-2" />
            <p className="text-2xl font-bold text-green-500">{stats.live}</p>
            <p className="text-sm text-muted-foreground">Live Now</p>
          </div>
          <div className="glass-card p-5">
            <Clock className="w-7 h-7 text-blue-500 mb-2" />
            <p className="text-2xl font-bold">{stats.upcoming}</p>
            <p className="text-sm text-muted-foreground">Upcoming</p>
          </div>
          <div className="glass-card p-5">
            <Trophy className="w-7 h-7 text-yellow-500 mb-2" />
            <p className="text-2xl font-bold">{stats.completed}</p>
            <p className="text-sm text-muted-foreground">Completed</p>
          </div>
          <div className="glass-card p-5">
            <DollarSign className="w-7 h-7 text-emerald-500 mb-2" />
            <p className="text-2xl font-bold">{stats.total_bids}</p>
            <p className="text-sm text-muted-foreground">Total Bids</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto">
          {['all', 'live', 'upcoming', 'completed', 'paused'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap ${
                activeTab === tab ? 'bg-orange-500 text-white' : 'bg-white/10 hover:bg-white/20'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Auctions List */}
        <div className="glass-card p-6">
          <h2 className="text-xl font-bold mb-4">Auctions ({auctions.length})</h2>
          
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto"></div>
            </div>
          ) : auctions.length > 0 ? (
            <div className="space-y-4">
              {auctions.map((auction) => (
                <div key={auction.id} className={`p-4 rounded-xl border ${
                  auction.status === 'live' ? 'border-green-500/30 bg-green-500/5' :
                  auction.status === 'upcoming' ? 'border-blue-500/30 bg-blue-500/5' :
                  'border-white/10 bg-white/5'
                }`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      {auction.images?.[0] ? (
                        <img src={getImageUrl(auction.images[0])} alt="" className="w-16 h-16 rounded-lg object-cover" />
                      ) : (
                        <div className="w-16 h-16 rounded-lg bg-orange-500/20 flex items-center justify-center">
                          <Gavel className="w-8 h-8 text-orange-500" />
                        </div>
                      )}
                      <div>
                        <h3 className="font-bold text-lg">{auction.title}</h3>
                        <p className="text-sm text-muted-foreground">
                          Start: ₹{auction.starting_bid?.toLocaleString()} | 
                          Current: ₹{auction.current_bid?.toLocaleString() || auction.starting_bid?.toLocaleString()} |
                          Bids: {auction.total_bids || 0}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {auction.start_time} → {auction.end_time}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        auction.status === 'live' ? 'bg-green-500/20 text-green-500' :
                        auction.status === 'upcoming' ? 'bg-blue-500/20 text-blue-500' :
                        auction.status === 'completed' ? 'bg-yellow-500/20 text-yellow-500' :
                        'bg-gray-500/20 text-gray-500'
                      }`}>
                        {auction.status}
                      </span>
                      {auction.allow_free_members && (
                        <span className="px-2 py-1 rounded-full text-[10px] font-bold bg-green-500/20 text-green-400 border border-green-500/30">
                          FREE MEMBERS
                        </span>
                      )}
                      {auction.status === 'live' && (
                        <button
                          onClick={() => navigate(`/admin/auction/${auction.id}/room`)}
                          className="px-3 py-2 rounded-lg bg-orange-500 text-white hover:bg-orange-600 flex items-center gap-1 text-sm font-medium"
                          title="Enter Auction Room"
                        >
                          <Radio className="w-4 h-4" /> Enter Room
                        </button>
                      )}
                      {(auction.status === 'live' || auction.status === 'paused') && (
                        <button
                          onClick={() => toggleAuctionStatus(auction.id, auction.status)}
                          className={`p-2 rounded-lg ${auction.status === 'live' ? 'bg-yellow-500/20 text-yellow-500' : 'bg-green-500/20 text-green-500'}`}
                          title={auction.status === 'live' ? 'Pause' : 'Resume'}
                        >
                          {auction.status === 'live' ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                        </button>
                      )}
                      <button
                        onClick={() => navigate(`/admin/auction/${auction.id}/room`)}
                        className="p-2 rounded-lg bg-purple-500/20 text-purple-500 hover:bg-purple-500/30"
                        title="View Room"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => openEditModal(auction)}
                        className="p-2 rounded-lg bg-blue-500/20 text-blue-500 hover:bg-blue-500/30"
                        title="Edit"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => deleteAuction(auction.id)}
                        className="p-2 rounded-lg bg-red-500/20 text-red-500 hover:bg-red-500/30"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Gavel className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">No auctions found</p>
              <button 
                onClick={() => setShowCreateModal(true)}
                className="mt-4 px-4 py-2 bg-orange-500 text-white rounded-lg"
              >
                Create First Auction
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Modal */}
      {(showCreateModal || editingAuction) && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-background border border-white/10 rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">
                {editingAuction ? 'Edit Auction' : 'Create New Auction'}
              </h2>
              {!editingAuction && (
                <button
                  type="button"
                  onClick={() => setShowAIImport(!showAIImport)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                    showAIImport 
                      ? 'bg-purple-500 text-white' 
                      : 'bg-purple-500/20 text-purple-400 hover:bg-purple-500/30'
                  }`}
                >
                  <Sparkles className="w-4 h-4" />
                  AI Import
                </button>
              )}
            </div>
            
            {/* AI Import Widget */}
            {showAIImport && !editingAuction && (
              <AIImportWidget 
                onDataExtracted={handleAIImportData}
                listingType="auction"
              />
            )}
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm mb-2">Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-4 py-3 bg-white/10 border border-white/10 rounded-xl"
                  placeholder="Auction title"
                />
              </div>
              
              <div>
                <label className="block text-sm mb-2">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-3 bg-white/10 border border-white/10 rounded-xl"
                  rows={3}
                  placeholder="Describe the item"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm mb-2">Starting Bid (₹) *</label>
                  <input
                    type="number"
                    value={formData.starting_bid}
                    onChange={(e) => setFormData({ ...formData, starting_bid: parseInt(e.target.value) || 0 })}
                    className="w-full px-4 py-3 bg-white/10 border border-white/10 rounded-xl"
                    min="1"
                  />
                </div>
                <div>
                  <label className="block text-sm mb-2">Bid Increment (₹) *</label>
                  <input
                    type="number"
                    value={formData.bid_increment}
                    onChange={(e) => setFormData({ ...formData, bid_increment: parseInt(e.target.value) || 10 })}
                    className="w-full px-4 py-3 bg-white/10 border border-white/10 rounded-xl"
                    min="1"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm mb-2">Reserve Price (₹)</label>
                  <input
                    type="number"
                    value={formData.reserve_price}
                    onChange={(e) => setFormData({ ...formData, reserve_price: parseInt(e.target.value) || 0 })}
                    className="w-full px-4 py-3 bg-white/10 border border-white/10 rounded-xl"
                    min="0"
                  />
                </div>
                <div>
                  <label className="block text-sm mb-2">Buy Now Price (₹)</label>
                  <input
                    type="number"
                    value={formData.buy_now_price}
                    onChange={(e) => setFormData({ ...formData, buy_now_price: parseInt(e.target.value) || 0 })}
                    className="w-full px-4 py-3 bg-white/10 border border-white/10 rounded-xl"
                    min="0"
                    placeholder="0 to disable"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm mb-2">Start Time *</label>
                  <input
                    type="datetime-local"
                    value={formData.start_time}
                    onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                    className="w-full px-4 py-3 bg-white/10 border border-white/10 rounded-xl"
                  />
                </div>
                <div>
                  <label className="block text-sm mb-2">End Time *</label>
                  <input
                    type="datetime-local"
                    value={formData.end_time}
                    onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                    className="w-full px-4 py-3 bg-white/10 border border-white/10 rounded-xl"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm mb-2">Category</label>
                  <select
                    value={formData.category_id}
                    onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                    className="w-full px-4 py-3 bg-white/10 border border-white/10 rounded-xl"
                  >
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>{cat.charAt(0).toUpperCase() + cat.slice(1)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm mb-2">Entry Pass Tier</label>
                  <select
                    value={formData.entry_pass_tier}
                    onChange={(e) => setFormData({ ...formData, entry_pass_tier: e.target.value })}
                    className="w-full px-4 py-3 bg-white/10 border border-white/10 rounded-xl"
                  >
                    <option value="SILVER">Silver</option>
                    <option value="GOLD">Gold</option>
                    <option value="DIAMOND">Diamond</option>
                    <option value="VIP">VIP</option>
                    <option value="PREMIUM">Premium</option>
                  </select>
                </div>
              </div>
              
              {/* Allow Free Members Toggle */}
              <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-xl">
                <label className="flex items-center justify-between cursor-pointer">
                  <div>
                    <span className="font-medium text-green-400">Allow Free Members</span>
                    <p className="text-xs text-muted-foreground mt-1">
                      Enable this to allow non-activated members to participate without a pass
                    </p>
                  </div>
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={formData.allow_free_members}
                      onChange={(e) => setFormData({ ...formData, allow_free_members: e.target.checked })}
                      className="sr-only"
                    />
                    <div className={`w-14 h-7 rounded-full transition-colors ${formData.allow_free_members ? 'bg-green-500' : 'bg-white/20'}`}>
                      <div className={`w-6 h-6 rounded-full bg-white shadow-md transform transition-transform ${formData.allow_free_members ? 'translate-x-7' : 'translate-x-0.5'} mt-0.5`} />
                    </div>
                  </div>
                </label>
              </div>
              
              <div>
                <label className="block text-sm mb-2">Image URL</label>
                <div className="flex gap-2">
                  <input
                    type="url"
                    value={formData.image_url}
                    onChange={(e) => setFormData({ ...formData, image_url: e.target.value, images: e.target.value ? [e.target.value] : [] })}
                    className="flex-1 px-4 py-3 bg-white/10 border border-white/10 rounded-xl"
                    placeholder="https://..."
                  />
                  <button
                    type="button"
                    onClick={() => setShowDrivePicker(true)}
                    className="px-4 py-3 bg-green-500/20 text-green-400 rounded-xl hover:bg-green-500/30 flex items-center gap-2"
                  >
                    <HardDrive className="w-5 h-5" />
                    Drive
                  </button>
                </div>
              </div>
              
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => { setShowCreateModal(false); setEditingAuction(null); resetForm(); }}
                  className="flex-1 py-3 bg-white/10 rounded-xl font-semibold hover:bg-white/20"
                >
                  Cancel
                </button>
                <button
                  onClick={editingAuction ? updateAuction : createAuction}
                  className="flex-1 py-3 bg-orange-500 text-white rounded-xl font-semibold hover:bg-orange-600"
                >
                  {editingAuction ? 'Update Auction' : 'Create Auction'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Google Drive Picker Modal */}
      {showDrivePicker && (
        <GoogleDrivePicker
          onSelect={handleDriveSelect}
          onClose={() => setShowDrivePicker(false)}
          imagesOnly={true}
        />
      )}
    </div>
  );
};

export default AuctionAdmin;
